/**
 * Image Optimization Utilities
 * Compressão/redimensionamento (canvas → WebP/JPEG) e validação de upload.
 */

export interface CompressionOptions {
  maxWidth?: number;
  maxHeight?: number;
  quality?: number; // 0-1 (0.7 = 70%)
  format?: 'jpeg' | 'webp';
}

/**
 * Comprime uma imagem, redimensionando e convertendo para WebP/JPEG.
 *
 * @example
 * const compressed = await compressImage(file, { maxWidth: 1920, quality: 0.75, format: 'webp' });
 */
export async function compressImage(
  file: File,
  options: CompressionOptions = {}
): Promise<File> {
  const {
    maxWidth = 1920,
    maxHeight = 1920,
    quality = 0.75,
    format = 'webp',
  } = options;

  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onerror = () => reject(new Error('Erro ao ler arquivo'));

    reader.onload = (e) => {
      const img = new Image();

      img.onerror = () => reject(new Error('Erro ao carregar imagem'));

      img.onload = () => {
        const canvas = document.createElement('canvas');
        let { width, height } = img;

        // Redimensionar mantendo proporção
        if (width > maxWidth) {
          height = Math.round((height * maxWidth) / width);
          width = maxWidth;
        }

        if (height > maxHeight) {
          width = Math.round((width * maxHeight) / height);
          height = maxHeight;
        }

        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Não foi possível obter contexto 2D do canvas'));
          return;
        }

        // Anti-aliasing
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0, width, height);

        const mimeType = format === 'webp' ? 'image/webp' : 'image/jpeg';
        const ext = format === 'webp' ? 'webp' : 'jpg';

        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Erro ao converter canvas para blob'));
              return;
            }

            const fileName = `${file.name.replace(/\.[^.]+$/, '')}.${ext}`;
            resolve(
              new File([blob], fileName, { type: mimeType, lastModified: Date.now() })
            );
          },
          mimeType,
          quality
        );
      };

      img.src = e.target?.result as string;
    };

    reader.readAsDataURL(file);
  });
}

/**
 * Valida arquivo de imagem (tamanho e tipo).
 */
export function validateImageUpload(
  file: File,
  options: {
    maxSizeMB?: number;
    allowedTypes?: string[];
  } = {}
): { valid: boolean; error?: string } {
  const { maxSizeMB = 10, allowedTypes = ['image/jpeg', 'image/png', 'image/webp'] } = options;

  const maxBytes = maxSizeMB * 1024 * 1024;

  if (file.size > maxBytes) {
    return {
      valid: false,
      error: `Arquivo excede ${maxSizeMB}MB (arquivo tem ${(file.size / 1024 / 1024).toFixed(2)}MB)`,
    };
  }

  if (!allowedTypes.includes(file.type)) {
    return {
      valid: false,
      error: `Tipo de arquivo inválido. Permitidos: ${allowedTypes.join(', ')}`,
    };
  }

  return { valid: true };
}
